import api from './api';

export interface LoginRequest {
  username: string;
  password: string;
  grant_type?: string;
  scope?: string;
  client_id?: string;
  client_secret?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

export interface UserMeta {
  objects_count: number;
  zones_count: number;
  sensors_count: number;
  header?: Array<{
    measurement_id: string;
    measurement_name: string;
    measurement_unit: string;
  }>;
}

export interface UserResponse {
  id: number;
  email: string;
  role: 'admin' | 'user';
  fullname?: string;
  whatsapp_number?: string | null;
  created_at: string;
  updated_at: string;
  objects?: Array<unknown>;
  meta?: UserMeta;
}

export interface FileUploadResponse {
  file_id: string;
}

export interface BulkCreateRequest {
  name: string;
  email: string;
  password: string;
  objects: Array<{
    name: string;
    file_id?: string;
    zones: Array<{
      name: string;
      sensors: Array<{
        sensor_type: string;
        sensor_eui: string;
        plant_type: string;
        plant_name: string;
      }>;
    }>;
  }>;
}

export interface BulkCreateResponse {
  success: boolean;
  message?: string;
}

export interface GetUsersResponse {
  users: UserResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface GetUserResponse {
  id: number;
  fullname: string;
  email: string;
  role: 'admin' | 'user';
  whatsapp_number?: string;
  whatsapp_notifications_enabled?: boolean;
  created_at: string;
  updated_at: string;
  objects?: Array<{
    id: number;
    name: string;
    file_url: string;
    file_id: string;
    original_filename?: string;
    zones: Array<{
      id: number;
      name: string;
      sensors: Array<{
        id: number;
        sensor_type: string;
        sensor_eui: string;
        plant_type: string;
        plant_name: string;
        soil_type: string;
        active?: boolean;
      }>;
    }>;
  }>;
}

export interface UpdatePersonalInfoRequest {
  email?: string;
  password?: string;
  fullname?: string;
  whatsapp_number?: string;
  whatsapp_notifications_enabled?: boolean;
}

export interface UpdateUserRequest {
  id: number;
  name?: string;
  email?: string;
  password?: string;
  whatsapp_number?: string;
  whatsapp_notifications_enabled?: boolean;
  objects?: Array<{
    id?: number;
    name: string;
    file_id?: string;
    zones: Array<{
      id?: number;
      name: string;
      sensors: Array<{
        id?: number;
        sensor_type: string;
        sensor_eui: string;
        plant_type: string;
        plant_name: string;
        active?: boolean;
      }>;
    }>;
  }>;
}

export interface UpdateUserResponse {
  success: boolean;
  message?: string;
}

export interface ValidateEmailRequest {
  email: string;
}

export interface ValidateEmailResponse {
  email: string;
  exists: boolean;
}

export interface RegisterUserRequest {
  email: string;
  password: string;
  fullname?: string;
  whatsapp_number?: string;
  whatsapp_notifications_enabled?: boolean;
}

export interface RegisterUserResponse {
  id: number;
  email: string;
  role: 'admin' | 'user';
  fullname?: string;
  whatsapp_number?: string;
  created_at: string;
  updated_at: string;
}

export interface AttachHierarchyRequest {
  user_id: number;
  objects: Array<{
    name: string;
    file_id?: string;
    zones: Array<{
      name: string;
      sensors: Array<{
        sensor_type: string;
        sensor_eui: string;
        plant_type: string;
        plant_name: string;
      }>;
    }>;
  }>;
}

export interface AttachHierarchyResponse {
  user_id: number;
  objects_created: number;
  zones_created: number;
  sensors_created: number;
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<TokenResponse> => {
    const formData = new URLSearchParams();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    if (credentials.grant_type) {
      formData.append('grant_type', credentials.grant_type);
    }
    if (credentials.scope) {
      formData.append('scope', credentials.scope);
    }
    if (credentials.client_id) {
      formData.append('client_id', credentials.client_id);
    }
    if (credentials.client_secret) {
      formData.append('client_secret', credentials.client_secret);
    }

    const response = await api.post<TokenResponse>(
      '/api/v1/auth/login',
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );
    return response.data;
  },

  getCurrentUser: async (): Promise<UserResponse> => {
    const response = await api.get<UserResponse>('/api/v1/auth/me');
    return response.data;
  },

  uploadFile: async (file: File): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('original_filename', file.name);

    const response = await api.post<FileUploadResponse>(
      '/api/v1/files/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  },

  bulkCreateUser: async (
    data: BulkCreateRequest,
  ): Promise<BulkCreateResponse> => {
    const response = await api.post<BulkCreateResponse>(
      'api/v1/auth/bulk-create',
      data,
    );
    return response.data;
  },

  getUsers: async (page = 1, limit = 10): Promise<GetUsersResponse> => {
    const response = await api.get<GetUsersResponse>(
      `/api/v1/users?page=${page}&limit=${limit}`,
    );
    return response.data;
  },

  getUser: async (id: number): Promise<GetUserResponse> => {
    const response = await api.get<GetUserResponse>(`/api/v1/users/${id}`);
    return response.data;
  },

  updateUser: async (data: UpdateUserRequest): Promise<UpdateUserResponse> => {
    const { id, ...updateData } = data;
    const response = await api.put<UpdateUserResponse>(
      `/api/v1/users/${id}`,
      updateData,
    );
    return response.data;
  },

  bulkUpdateUser: async (
    data: UpdateUserRequest,
  ): Promise<UpdateUserResponse> => {
    const { id, ...updateData } = data;
    const response = await api.patch<UpdateUserResponse>(
      `/api/v1/users/${id}/bulk-update`,
      updateData,
    );
    return response.data;
  },

  updatePersonalInfo: async (
    id: number,
    data: UpdatePersonalInfoRequest,
  ): Promise<UserResponse> => {
    const response = await api.patch<UserResponse>(
      `/api/v1/users/${id}/personal-info`,
      data,
    );
    return response.data;
  },

  validateEmail: async (
    data: ValidateEmailRequest,
  ): Promise<ValidateEmailResponse> => {
    const response = await api.post<ValidateEmailResponse>(
      '/api/v1/auth/validate-email',
      data,
    );
    return response.data;
  },

  registerUser: async (
    data: RegisterUserRequest,
  ): Promise<RegisterUserResponse> => {
    const response = await api.post<RegisterUserResponse>(
      '/api/v1/auth/register',
      data,
    );
    return response.data;
  },

  attachHierarchy: async (
    data: AttachHierarchyRequest,
  ): Promise<AttachHierarchyResponse> => {
    const response = await api.post<AttachHierarchyResponse>(
      '/api/v1/auth/attach-hierarchy',
      data,
    );
    return response.data;
  },
};
